// TanStack Query keys + fetchers for the SM-396 currency registry and
// the GREEN conversion operator surface (SM-444 / SM-467). Own file for
// the same reason wallet-queries.ts and ops-queries.ts are: the surface
// is self-contained and its cache keys should be invalidatable without
// touching anything else.
//
// Two upstreams, deliberately:
//
//   - the REGISTRY is read from `GET /admin/currencies` (SM-485), which
//     is permission-gated in backoffice-service AND again in ledger. It
//     is deliberately NOT F1-gated: F1 gates what a PLAYER can see,
//     while the operator switches have to be inspectable and settable
//     while the flag is still dark — that is the whole R2 gate. This
//     read is also the only one carrying updated_by and the four rail
//     limits, which the player projection omits.
//   - F1's state is still one of the four indicators SM-444 shows, so
//     it is PROBED separately: the gateway's public `GET /api/currencies`
//     answers 404 when the flag is dark (the route is not mounted at
//     all), and that 404 is the signal. Nothing is read from its body —
//     it is a flag probe, not a data source. nginx already tunnels /api/
//     to the gateway so cookies stay first-party.
//   - everything CONVERSION goes through `/admin/conversions/*`, which
//     is permission-gated in backoffice-service AND again in ledger.

import { api, APIError, qs } from "./api";
import { iconFormData, type MediaUploadResponse } from "./queries";
import type { PageInfo, Paginated } from "./types";
import type {
  ConversionPoolStatus,
  ConversionPoolStatusResponse,
  ConversionReconResponse,
  ConversionReconRouteTotals,
  ConversionRouteConfig,
  ConversionRouteConfigListResponse,
  ConversionSwitchChange,
  ConversionSwitchHistoryResponse,
  CreateConversionRateInput,
  CurrencyInfo,
  CurrencyListResponse,
  FundPoolInput,
  PoolMovementItem,
  RebalancePoolInput,
  UpdateConversionPoolInput,
  UpdateConversionRouteInput,
  UpdateCurrencyInput,
} from "./currency-types";

export const currencyKeys = {
  all: ["currency"] as const,
  registry: () => [...currencyKeys.all, "registry"] as const,
  playerReadFlag: () => [...currencyKeys.all, "player-read-flag"] as const,
  routes: () => [...currencyKeys.all, "conversion-routes"] as const,
  pools: () => [...currencyKeys.all, "conversion-pools"] as const,
  poolMovements: (currency: string, offset: number, limit: number) =>
    [...currencyKeys.all, "pool-movements", currency, offset, limit] as const,
  recon: () => [...currencyKeys.all, "conversion-recon"] as const,
  switches: (limit: number) => [...currencyKeys.all, "conversion-switches", limit] as const,
};

/**
 * The operator registry read (SM-485). Requires CURRENCY_VIEW, carries
 * the four rail limits and the audit pair, and answers whether or not
 * F1 is dark.
 *
 * Errors are NOT swallowed here: a failed operator read is a real fault
 * the page must show as one. The old swallow-404 behaviour belonged to
 * the public route, which is now probed separately below.
 */
export const fetchCurrencyRegistry = () =>
  api<CurrencyListResponse>("/admin/currencies").then<CurrencyInfo[]>((r) => r.currencies ?? []);

/**
 * F1's state, probed rather than inferred.
 *
 * The gateway does not mount the player read at all while the flag is
 * dark, so chi answers with a plain 404 carrying no error envelope —
 * hence the status check rather than a code check. Nothing is read from
 * the body: this exists so the page can show the feature-flag layer as
 * one of SM-444's four SEPARATE indicators, now that the values
 * themselves come from a route the flag does not gate.
 *
 * Any other failure resolves to `false` too. An operator cannot act on
 * "the probe errored", and reporting the flag as unknown-but-probably-on
 * would be the one direction that misleads: it would present a dark rail
 * as live.
 */
export const fetchPlayerCurrencyReadEnabled = (): Promise<boolean> =>
  api<CurrencyListResponse>("/api/currencies")
    .then(() => true)
    .catch((e) => {
      if (e instanceof APIError) return false;
      return false;
    });

/**
 * POST /admin/currencies/{code}/image — artwork for a currency's mark.
 *
 * Returns a URL; it does NOT set the field. The edit form puts that URL into
 * its draft and saves it with everything else, so artwork lands through the
 * same validated, audited PUT as the name and the colour — and abandoning the
 * form leaves an orphaned blob rather than a half-changed registry.
 *
 * Needs CURRENCY_CONFIG, not CURRENCY_VIEW: an upload is the first half of a
 * write, and a read-only operator must not be able to put objects in the
 * platform's blob store.
 */
export const uploadCurrencyImage = (code: string, file: File) =>
  api<MediaUploadResponse>(`/admin/currencies/${encodeURIComponent(code)}/image`, {
    method: "POST",
    body: iconFormData(file),
  });

/**
 * PUT /admin/currencies/{code} (SM-485). Sends ONLY the fields that
 * changed — an omitted field means "leave alone", and sending the whole
 * row would make every save a claim about every field.
 *
 * The immutable fields (code, role, the *_ceiling bounds, conversion
 * routes) are absent from UpdateCurrencyInput by construction, so this
 * call cannot express one. Ledger refuses them by name anyway; both
 * halves are deliberate.
 */
export const updateCurrency = (code: string, input: UpdateCurrencyInput) =>
  api<CurrencyInfo>(`/admin/currencies/${encodeURIComponent(code)}`, {
    method: "PUT",
    body: input,
  });

export const fetchConversionRoutes = () =>
  api<ConversionRouteConfigListResponse>("/admin/conversions/routes").then<ConversionRouteConfig[]>(
    (r) => r.routes ?? [],
  );

export const fetchConversionPools = () =>
  api<ConversionPoolStatusResponse>("/admin/conversions/pools").then<ConversionPoolStatus[]>(
    (r) => r.pools ?? [],
  );

export const fetchConversionRecon = () =>
  api<ConversionReconResponse>("/admin/conversions/recon").then<ConversionReconRouteTotals[]>(
    (r) => r.routes ?? [],
  );

export const fetchPoolMovements = (
  currency: string,
  opts: { limit: number; offset: number },
) =>
  api<{ movements: PoolMovementItem[]; page?: PageInfo }>(
    `/admin/conversions/pools/${encodeURIComponent(currency)}/movements${qs({
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  ).then<Paginated<PoolMovementItem>>((r) => {
    const items = r.movements ?? [];
    // An upstream that omits the envelope still has to produce an
    // honest `has_more`, or the pager stops one page early and the
    // movement history quietly loses its tail. A full page means there
    // is probably more; a short one is definitively the end.
    return {
      items,
      page: r.page ?? {
        limit: opts.limit,
        offset: opts.offset,
        total: opts.offset + items.length,
        has_more: items.length >= opts.limit,
      },
    };
  });

/**
 * Fund / rebalance carry the operator's idempotency reference in the
 * BODY (`operation_id`), which is what ledger's duplicate guard keys on.
 * The caller mints it once per confirm — not per attempt — so a retry
 * after a lost response resolves the original movement instead of making
 * a second one.
 */
export const fundConversionPool = (currency: string, input: FundPoolInput) =>
  api<PoolMovementItem>(`/admin/conversions/pools/${encodeURIComponent(currency)}/fund`, {
    method: "POST",
    body: input,
    idempotencyKey: input.operation_id,
  });

export const rebalanceConversionPool = (currency: string, input: RebalancePoolInput) =>
  api<PoolMovementItem>(`/admin/conversions/pools/${encodeURIComponent(currency)}/rebalance`, {
    method: "POST",
    body: input,
    idempotencyKey: input.operation_id,
  });

export const updateConversionRoute = (route: string, input: UpdateConversionRouteInput) =>
  api<ConversionRouteConfig>(`/admin/conversions/routes/${encodeURIComponent(route)}`, {
    method: "PUT",
    body: input,
  });

/**
 * The pool master switch (SM-524). A PUT with the flip and the operator
 * reason; the server refuses a reasonless flip and records the change
 * either way, so nothing here needs to remember it locally.
 *
 * The correlation id doubles as the idempotency reference: a retried
 * confirm after a lost response sets the same state with the same id
 * rather than logging a second flip.
 */
export const updateConversionPool = (currency: string, input: UpdateConversionPoolInput) =>
  api<ConversionPoolStatus>(`/admin/conversions/pools/${encodeURIComponent(currency)}`, {
    method: "PUT",
    body: input,
    idempotencyKey: input.correlation_id,
  });

/** The append-only pool/route switch history, newest first. */
export const fetchConversionSwitches = (limit: number) =>
  api<ConversionSwitchHistoryResponse>(`/admin/conversions/switches${qs({ limit, offset: 0 })}`)
    .then<ConversionSwitchChange[]>((r) => r.changes ?? []);

export const createConversionRate = (route: string, input: CreateConversionRateInput) =>
  api<ConversionRouteConfig>(`/admin/conversions/routes/${encodeURIComponent(route)}/rates`, {
    method: "POST",
    body: input,
    idempotencyKey: input.operation_id,
  });
